import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { cloneElement, ReactNode, useState } from 'react';
import { animated, useTransition } from '@react-spring/web';

interface Props {
  label: JSX.Element;
  content: ReactNode;
  contentClassName?: string;
  placement?: Placement;
}

export const Tooltip = ({
  content,
  label,
  placement = 'top',
  contentClassName,
}: Props) => {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { restMs: 20, delay: { open: 1000, close: 100 } }),
    useFocus(context),
    useRole(context, { role: 'tooltip' }),
    useDismiss(context),
  ]);

  const transitions = useTransition(open, {
    from: {
      opacity: 0.9,
      scale: 0.9,
    },
    enter: {
      opacity: 1,
      scale: 1,
    },
    leave: {
      opacity: 0,
      scale: 0.9,
    },
    config: {
      duration: 100,
      // easing: easings.easeInBounce,
    },
    delay: 10,
  });

  const contentHasPadding = contentClassName?.includes('p-');

  const floatingProps = getFloatingProps({
    ref: refs.setFloating,
    className: `normal-case text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-md shadow-lg ${
      contentClassName ? contentClassName : ''
    } ${!contentHasPadding ? 'p-2' : ''}`,
    style: floatingStyles,
  });

  return (
    <>
      {cloneElement(
        label,
        getReferenceProps({
          ref: refs.setReference,
          ...label.props,
          children: label.props.children,
        })
      )}
      {transitions(
        (styles, show) =>
          show && (
            <animated.div
              {...floatingProps}
              style={{ ...(floatingProps.style as any), ...styles }}
            >
              {content}
            </animated.div>
          )
      )}
    </>
  );
};

export default Tooltip;
